import { Spacing } from 'zens-storybook'
import React, { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { AccessType, Draft, Workspace, useStore, useZoneWorkspacesMutation } from 'zens-store'
import { useNumberParams, usePaginationParam, useSortParam, useBooleanParams } from 'zens-router'
import { ZoneInfo, LayoutHeader, ZoneWorkspaceTable, ZoneHeader } from '@dms/component'
import { usePermissions } from '@dms/hook'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    margin: Spacing.L,
  },
  workspaces: {
    marginTop: Spacing.XL,
  },
})

const ZoneDetail = () => {
  const { message } = useStore()
  const { t } = useTranslation()
  const [{ companyId, zoneId }] = useNumberParams()
  const [{ workspaces }] = useBooleanParams()
  const [pagination, setPagination] = usePaginationParam()
  const [sort, setSort] = useSortParam({ field: 'name', direction: 'asc' })
  const mutation = useZoneWorkspacesMutation({ companyId, zoneId })
  const { hasPermission } = usePermissions(companyId)

  async function addWorkspaces(data: Workspace[]) {
    try {
      await mutation.add(new Draft<any>({ id: zoneId, workspaces: data.map((workspace) => workspace.id) }))
      message.add({
        message: t('Workspaces added to zone succesfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`ZONE: Add workspace failed: ${errorMessage}`)
      message.add({
        message: `${t('Workspaces could not be added')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }

  async function removeWorkspace(workspace: Workspace) {
    try {
      await mutation.delete(new Draft(workspace.data))
      message.add({
        message: t('Workspace was removed from zone succesfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`ZONE: Remove workspace failed: ${errorMessage}`)
      message.add({
        message: `${t('Workspace could not be removed')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <ZoneHeader companyId={companyId} zoneId={zoneId} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<ZoneInfo.Skeleton companyId={companyId} />}>
          <ZoneInfo
            companyId={companyId}
            zoneId={zoneId}
            buttonHref={
              hasPermission(AccessType.Update, 'workspace')
                ? `/companies/${companyId}/workspaces/zones/${zoneId}/edit`
                : undefined
            }
          />
        </Suspense>
        <Suspense
          fallback={<ZoneWorkspaceTable.Skeleton style={styles.workspaces} companyId={companyId} zoneId={zoneId} />}
        >
          <ZoneWorkspaceTable
            style={styles.workspaces}
            companyId={companyId}
            zoneId={zoneId}
            pagination={pagination}
            onPaginationChange={setPagination}
            sort={sort}
            onSortChange={setSort}
            showAddWorkspaces={workspaces}
            onRemove={removeWorkspace}
            onAdd={addWorkspaces}
          />
        </Suspense>
      </View>
    </View>
  )
}

export default ZoneDetail
